import api from './axios';

/* ── User signup — step 1 of 2 ──────────────────────────────────
   POST /api/auth/signup
   No longer returns a token: it parks the registration and emails a
   6-digit code. The account does not exist until verifySignup runs.
   Resolves to { email, ttlMinutes, resendCooldownSeconds }.
─────────────────────────────────────────────────────────────── */
export const signup = async ({ name, email, password }) => {
  const { data } = await api.post('/auth/signup', { name, email, password });
  return data;
};

/* ── User signup — step 2 of 2 ──────────────────────────────────
   POST /api/auth/verify-signup
   Trades the emailed code for a real account plus a login token.
─────────────────────────────────────────────────────────────── */
export const verifySignup = async ({ email, otp }) => {
  const { data } = await api.post('/auth/verify-signup', { email, otp });
  return data;
};

/* ── Resend the signup code ─────────────────────────────────────
   POST /api/auth/resend-signup-otp   (rate limited server-side)
─────────────────────────────────────────────────────────────── */
export const resendSignupOtp = async ({ email }) => {
  const { data } = await api.post('/auth/resend-signup-otp', { email });
  return data;
};

/* ── User login ─────────────────────────────────────────────────
   POST /api/auth/login
   body: { email, password }
   returns: { success, token, user }
─────────────────────────────────────────────────────────────── */
export const login = async ({ email, password }) => {
  const { data } = await api.post('/auth/login', { email, password });
  return data;
};

/* ── Google sign-in ────────────────────────────────────────────
   POST /api/auth/google
   body: { credential }
   returns: { success, token, user }
─────────────────────────────────────────────────────────────── */
export const googleAuth = async ({ credential }) => {
  const { data } = await api.post('/auth/google', { credential });
  return data;
};

/* ── Admin login ────────────────────────────────────────────────
   POST /api/auth/admin/login
   body: { email, password }
   returns: { success, token, user }
─────────────────────────────────────────────────────────────── */
export const adminLogin = async ({ email, password }) => {
  const { data } = await api.post('/auth/admin/login', { email, password });
  return data;
};

/* ── Get current user ───────────────────────────────────────────
   GET /api/auth/me
   returns: { success, user }
─────────────────────────────────────────────────────────────── */
export const getMe = async () => {
  const { data } = await api.get('/auth/me');
  return data;
};

/* ── Forgot password — step 1 ───────────────────────────────────
   POST /api/auth/forgot-password
   body: { email }
   Emails a 5-digit OTP. Always reports success, whether or not the
   address has an account, so it can't be used to probe for users.
─────────────────────────────────────────────────────────────── */
export const forgotPassword = async ({ email }) => {
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
};

/* ── Forgot password — step 2 ───────────────────────────────────
   POST /api/auth/verify-reset-otp
   body: { email, otp }
   returns: { success, resetToken } — feed the token to resetPassword()
─────────────────────────────────────────────────────────────── */
export const verifyResetOtp = async ({ email, otp }) => {
  const { data } = await api.post('/auth/verify-reset-otp', { email, otp });
  return data;
};

/* ── Reset password ─────────────────────────────────────────────
   PATCH /api/auth/reset-password/:token
   body: { password }
─────────────────────────────────────────────────────────────── */
export const resetPassword = async ({ token, password }) => {
  const { data } = await api.patch(`/auth/reset-password/${token}`, { password });
  return data;
};

/* ── Update password (logged in) ────────────────────────────────
   PATCH /api/auth/update-password
   body: { currentPassword, newPassword }
─────────────────────────────────────────────────────────────── */
/* ── Rename the account ─────────────────────────────────────────
   PATCH /api/auth/me   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const updateProfile = async ({ name }) => {
  const { data } = await api.patch('/auth/me', { name });
  return data;
};

/* ── Delete the account and everything attached to it ───────────
   DELETE /api/auth/me   🔒 Requires user token + the password
─────────────────────────────────────────────────────────────── */
export const deleteAccount = async ({ password }) => {
  const { data } = await api.delete('/auth/me', { data: { password } });
  return data;
};

export const updatePassword = async ({ currentPassword, newPassword }) => {
  const { data } = await api.patch('/auth/update-password', { currentPassword, newPassword });
  return data;
};
